/**
 * @file AgencyFinalCta.jsx
 * @description Shared agency signup handler for the agency marketing pages: track the agency signup
 *   click, set the agency signup intent, navigate to auth, and expose the CTA label (promo or paid).
 *   The closing gradient CTA band that used to live here was removed (founder rule: ONE call to action
 *   per page, the hero); pages now end on the founder card or a quiet "Questions? Contact support." line.
 * @module components/AgencyFinalCta
 */
import { useTranslation } from "react-i18next";
import { useConfigVersion } from "../lib/useConfigVersion";
import { setSignupIntent } from "../lib/signupIntent";
import { track, A, ctaMeta, landingMeta } from "../lib/analytics";
import { getAgencyPricing } from "../lib/agencyPricing";

/** Returns the CTA label and a click handler (position -> tracks, sets intent, goes to auth). */
export function useAgencySignup({ source, pagePath, market, onNavigate, onSignUp }) {
  useConfigVersion(); // re-render with the Admin promo state once platform_config loads
  const { t, i18n } = useTranslation();
  const label = t(getAgencyPricing(i18n.language).isPromoActive ? "agencyPricing.cta" : "agencyPricing.ctaPaid");
  const go = (position = "final") => {
    if (onSignUp) { onSignUp(); return; }
    track(A.AGENCY_SIGNUP_CLICK, {
      supabaseMeta: landingMeta(source, { cta_position: position }),
      ga4Params: ctaMeta({ pagePath, pageType: "agency", ctaLabel: "Post your first case free", targetPath: "/auth", userLanguage: i18n.language, market, audience: "agency" }),
    });
    setSignupIntent("agency");
    onNavigate("auth"); // App sets authInitialMode="signup" for this hand-off
  };
  return { label, go };
}
